// Lightweight HTML sanitizer for rendering static scheme content safely.
// Removes scriptable elements, event handlers, and unsafe URL protocols.

const DISALLOWED_TAGS = new Set([
  "script",
  "iframe",
  "object",
  "embed",
  "base",
])

const URL_ATTRS = new Set(["href", "src", "srcset"])

function isUnsafeUrl(url: string): boolean {
  const trimmed = url.trim().toLowerCase()
  return trimmed.startsWith("javascript:") || trimmed.startsWith("data:")
}

function sanitizeElement(el: Element) {
  // Drop disallowed tags entirely
  if (DISALLOWED_TAGS.has(el.tagName.toLowerCase())) {
    el.remove()
    return
  }
  // Remove event handler attributes (on*)
  for (const attr of Array.from(el.attributes)) {
    const name = attr.name.toLowerCase()
    if (name.startsWith("on")) {
      el.removeAttribute(attr.name)
      continue
    }
    if (URL_ATTRS.has(name)) {
      const val = attr.value
      // For srcset, validate each candidate URL
      if (name === "srcset") {
        const parts = val.split(",")
        const safeParts = parts
          .map((p) => p.trim())
          .filter((p) => {
            const url = p.split(" ")[0]
            return url && !isUnsafeUrl(url)
          })
        if (safeParts.length > 0) el.setAttribute("srcset", safeParts.join(", "))
        else el.removeAttribute("srcset")
        continue
      }
      if (isUnsafeUrl(val)) {
        el.removeAttribute(attr.name)
      }
    }
  }
}

function walk(node: Node) {
  if (node.nodeType === Node.ELEMENT_NODE) {
    sanitizeElement(node as Element)
  }
  const children = Array.from(node.childNodes)
  for (const child of children) {
    walk(child)
  }
}

export function sanitizeHtml(html: string): string {
  try {
    const tpl = document.createElement("template")
    tpl.innerHTML = html
    walk(tpl.content)
    return tpl.innerHTML
  } catch {
    // On failure, return plain-text escaped content
    const div = document.createElement("div")
    div.textContent = html
    return div.innerHTML
  }
}